<div class="mx-auto p-6 bg-white border-2 border-gray rounded-md">
  <div class="flex justify-between items-center mb-6 border-b pb-4">
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ isNew ? "Add New Company" : "Edit Company Details" }}
    </h2>
    <button
      (click)="router.navigate(['/dashboard/companies'])"
      class="text-gray-500 hover:text-gray-700 transition-colors duration-200"
      aria-label="Close"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  </div>

  <app-loader
    [show]="isSubmitting"
    [mainMessage]="'Loading company data..'"
    [subMessage]="'Fetching company data from server'"
  >
  </app-loader>

  <form [formGroup]="companyForm" (ngSubmit)="onSubmit()" class="space-y-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="space-y-1">
        <label for="name" class="block text-sm font-medium text-gray-700"
          >Company Name (English) *</label
        >
        <input
          type="text"
          id="name"
          formControlName="name"
          [class.border-red-500]="
            companyForm.get('name')?.invalid && companyForm.get('name')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('name')?.invalid && companyForm.get('name')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("name") }}
        </div>
      </div>

      <div class="space-y-1">
        <label
          for="companyNameArabic"
          class="block text-sm font-medium text-gray-700"
          >Company Name (Arabic)</label
        >
        <input
          type="text"
          id="companyNameArabic"
          formControlName="companyNameArabic"
          [class.border-red-500]="
            companyForm.get('companyNameArabic')?.invalid &&
            companyForm.get('companyNameArabic')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('companyNameArabic')?.invalid &&
            companyForm.get('companyNameArabic')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("companyNameArabic") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="crNumber" class="block text-sm font-medium text-gray-700"
          >CR Number *</label
        >
        <input
          type="text"
          id="crNumber"
          formControlName="crNumber"
          [readonly]="!isNew"
          [class.border-red-500]="
            companyForm.get('crNumber')?.invalid &&
            companyForm.get('crNumber')?.touched
          "
          [class.bg-gray-100]="!isNew"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('crNumber')?.invalid &&
            companyForm.get('crNumber')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("crNumber") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="policyStartDate" class="block text-sm font-medium text-gray-700"
          >Policy Start Date *</label
        >
        <input
          type="date"
          id="policyStartDate"
          formControlName="policyStartDate"
          [class.border-red-500]="
            companyForm.get('policyStartDate')?.invalid &&
            companyForm.get('policyStartDate')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('policyStartDate')?.invalid &&
            companyForm.get('policyStartDate')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("policyStartDate") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="sponsorID" class="block text-sm font-medium text-gray-700"
          >Sponsor ID</label
        >
        <input
          type="text"
          id="sponsorID"
          formControlName="sponsorID"
          [class.border-red-500]="
            companyForm.get('sponsorID')?.invalid &&
            companyForm.get('sponsorID')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('sponsorID')?.invalid &&
            companyForm.get('sponsorID')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("sponsorID") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="vatNumber" class="block text-sm font-medium text-gray-700"
          >VAT Number</label
        >
        <input
          type="text"
          id="vatNumber"
          formControlName="vatNumber"
          placeholder="15-digit VAT number"
          [class.border-red-500]="
            companyForm.get('vatNumber')?.invalid &&
            companyForm.get('vatNumber')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('vatNumber')?.invalid &&
            companyForm.get('vatNumber')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("vatNumber") }}
        </div>
      </div>

      <div class="space-y-1">
        <label
          for="contactEmail"
          class="block text-sm font-medium text-gray-700"
          >Contact Email</label
        >
        <input
          type="email"
          id="contactEmail"
          formControlName="contactEmail"
          [class.border-red-500]="
            companyForm.get('contactEmail')?.invalid &&
            companyForm.get('contactEmail')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('contactEmail')?.invalid &&
            companyForm.get('contactEmail')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("contactEmail") }}
        </div>
      </div>

      <div class="space-y-1">
        <label
          for="contactPhone"
          class="block text-sm font-medium text-gray-700"
          >Contact Phone</label
        >
        <input
          type="tel"
          id="contactPhone"
          formControlName="contactPhone"
          placeholder="e.g., 0512345678 or +966512345678"
          [class.border-red-500]="
            companyForm.get('contactPhone')?.invalid &&
            companyForm.get('contactPhone')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('contactPhone')?.invalid &&
            companyForm.get('contactPhone')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("contactPhone") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="bankNameID" class="block text-sm font-medium text-gray-700"
          >Bank Name ID</label
        >
        <input
          type="text"
          id="bankNameID"
          formControlName="bankNameID"
          [class.border-red-500]="
            companyForm.get('bankNameID')?.invalid &&
            companyForm.get('bankNameID')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('bankNameID')?.invalid &&
            companyForm.get('bankNameID')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("bankNameID") }}
        </div>
      </div>

      <div class="space-y-1">
        <label for="ibanNumber" class="block text-sm font-medium text-gray-700"
          >IBAN Number</label
        >
        <input
          type="text"
          id="ibanNumber"
          formControlName="ibanNumber"
          placeholder="SA followed by 22 characters"
          [class.border-red-500]="
            companyForm.get('ibanNumber')?.invalid &&
            companyForm.get('ibanNumber')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('ibanNumber')?.invalid &&
            companyForm.get('ibanNumber')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("ibanNumber") }}
        </div>
      </div>

      <div class="space-y-1">
        <label
          for="entity700Number"
          class="block text-sm font-medium text-gray-700"
          >Entity 700 Number</label
        >
        <input
          type="text"
          id="entity700Number"
          formControlName="entity700Number"
          [class.border-red-500]="
            companyForm.get('entity700Number')?.invalid &&
            companyForm.get('entity700Number')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('entity700Number')?.invalid &&
            companyForm.get('entity700Number')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("entity700Number") }}
        </div>
      </div>

      <div class="space-y-1">
        <label
          for="numberOfEmployees"
          class="block text-sm font-medium text-gray-700"
          >Number of Employees</label
        >
        <input
          type="text"
          id="numberOfEmployees"
          formControlName="numberOfEmployees"
          [class.border-red-500]="
            companyForm.get('numberOfEmployees')?.invalid &&
            companyForm.get('numberOfEmployees')?.touched
          "
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
        />
        <div
          *ngIf="
            companyForm.get('numberOfEmployees')?.invalid &&
            companyForm.get('numberOfEmployees')?.touched
          "
          class="text-red-600 text-sm mt-1"
        >
          {{ getErrorMessage("numberOfEmployees") }}
        </div>
      </div>
    </div>

    <div class="flex justify-end space-x-4 pt-6 border-t">
      <button
        type="button"
        (click)="router.navigate(['/dashboard/companies'])"
        class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
      >
        Cancel
      </button>
      <button
        type="submit"
        [disabled]="isButtonDisabled()"
        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors duration-200"
        [ngClass]="{
          'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500': !isButtonDisabled(),
          'bg-gray-400 cursor-not-allowed': isButtonDisabled()
        }"
        [title]="getButtonDisabledReason()"
      >
        <span *ngIf="!isSubmitting && !isLoading">
          <i class="fas fa-save mr-2"></i>
          {{ isNew ? "Create Company" : "Update Company" }}
        </span>
        <span *ngIf="isSubmitting">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          {{ isNew ? "Creating..." : "Updating..." }}
        </span>
        <span *ngIf="isLoading">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Loading...
        </span>
      </button>
    </div>
  </form>
</div>
